var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, isValidElement } from 'react';
import set from 'lodash/set';
import { useChoices } from './useChoices';
import { useTranslate } from '../i18n';
/*
 * Returns helper functions for suggestions handling.
 *
 * @param allowDuplicates A boolean indicating whether a suggestion can be added several times
 * @param choices An array of available choices
 * @param emptyText The text to use for the empty suggestion. Defaults to an empty string
 * @param emptyValue The value to use for the empty suggestion. Defaults to `null`
 * @param limitChoicesToValue A boolean indicating whether the initial suggestions should be limited to the currently selected one(s)
 * @param matchSuggestion Optional unless `optionText` is a React element. Function which check whether a choice matches a filter. Must return a boolean.
 * @param optionText Either a string defining the property to use to get the choice text, a function or a React element
 * @param optionValue The property to use to get the choice value
 * @param selectedItem The currently selected item. May be an array of selected items
 * @param suggestionLimit The maximum number of suggestions returned
 * @param translateChoice A boolean indicating whether to option text should be translated
 *
 * @returns An object with helper functions:
 * - getChoiceText: Returns the choice text or a React element
 * - getChoiceValue: Returns the choice value
 * - getSuggestions: A function taking a filter value (string) and returning the matching suggestions
 */
export var useSuggestions = function (_a) {
    var allowCreate = _a.allowCreate, choices = _a.choices, _b = _a.createText, createText = _b === void 0 ? 'ra.action.create' : _b, _c = _a.createValue, createValue = _c === void 0 ? '@@create' : _c, _d = _a.emptyText, emptyText = _d === void 0 ? '' : _d, _e = _a.emptyValue, emptyValue = _e === void 0 ? null : _e, limitChoicesToValue = _a.limitChoicesToValue, matchSuggestion = _a.matchSuggestion, optionText = _a.optionText, optionValue = _a.optionValue, selectedItem = _a.selectedItem, _f = _a.suggestionLimit, suggestionLimit = _f === void 0 ? 0 : _f, translateChoice = _a.translateChoice;
    var translate = useTranslate();
    var _g = useChoices({
        optionText: optionText,
        optionValue: optionValue,
        translateChoice: translateChoice,
    }), getChoiceText = _g.getChoiceText, getChoiceValue = _g.getChoiceValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var getSuggestions = useCallback(getSuggestionsFactory({
        allowCreate: allowCreate,
        choices: choices,
        createText: createText,
        createValue: createValue,
        emptyText: translate(emptyText, { _: emptyText }),
        emptyValue: emptyValue,
        getChoiceText: getChoiceText,
        getChoiceValue: getChoiceValue,
        limitChoicesToValue: limitChoicesToValue,
        matchSuggestion: matchSuggestion,
        optionText: optionText,
        optionValue: optionValue,
        selectedItem: selectedItem,
        suggestionLimit: suggestionLimit,
    }), [
        allowCreate,
        choices,
        createText,
        createValue,
        emptyText,
        emptyValue,
        getChoiceText,
        getChoiceValue,
        limitChoicesToValue,
        matchSuggestion,
        optionText,
        optionValue,
        selectedItem,
        suggestionLimit,
        translate,
    ]);
    return {
        getChoiceText: getChoiceText,
        getChoiceValue: getChoiceValue,
        getSuggestions: getSuggestions,
    };
};
var escapeRegExp = function (value) {
    return value ? value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : '';
}; // $& means the whole matched string
/**
 * Default matcher implementation which check whether the suggestion text matches the filter.
 */
var defaultMatchSuggestion = function (getChoiceText) { return function (filter, suggestion, exact) {
    if (exact === void 0) { exact = false; }
    var suggestionText = getChoiceText(suggestion);
    var isReactElement = isValidElement(suggestionText);
    var regex = escapeRegExp(filter);
    return isReactElement
        ? false
        : suggestionText &&
            !!suggestionText.match(
            // We must escape any RegExp reserved characters to avoid errors
            // For example, the filter might contain * which must be escaped as \*
            new RegExp(exact ? "^".concat(regex, "$") : regex, 'i'));
}; };
/**
 * Get the suggestions to display after applying a fuzzy search on the available choices
 *
 * @example
 *
 * getSuggestions({
 *   choices: [{ id: 1, name: 'admin' }, { id: 2, name: 'publisher' }],
 *   optionText: 'name',
 *   optionValue: 'id',
 *   getSuggestionText: choice => choice[optionText],
 * })('pub')
 *
 * // Will return [{ id: 2, name: 'publisher' }]
 * getSuggestions({
 *   choices: [{ id: 1, name: 'admin' }, { id: 2, name: 'publisher' }],
 *   optionText: 'name',
 *   optionValue: 'id',
 *   getSuggestionText: choice => choice[optionText],
 * })('pub')
 *
 * // Will return [{ id: 2, name: 'publisher' }]
 */
export var getSuggestionsFactory = function (_a) {
    var _b = _a.allowCreate, allowCreate = _b === void 0 ? false : _b, _c = _a.choices, choices = _c === void 0 ? [] : _c, _d = _a.createText, createText = _d === void 0 ? 'ra.action.create' : _d, _e = _a.createValue, createValue = _e === void 0 ? '@@create' : _e, _f = _a.emptyText, emptyText = _f === void 0 ? '' : _f, _g = _a.emptyValue, emptyValue = _g === void 0 ? null : _g, _h = _a.optionText, optionText = _h === void 0 ? 'name' : _h, _j = _a.optionValue, optionValue = _j === void 0 ? 'id' : _j, getChoiceText = _a.getChoiceText, getChoiceValue = _a.getChoiceValue, _k = _a.limitChoicesToValue, limitChoicesToValue = _k === void 0 ? false : _k, _l = _a.matchSuggestion, matchSuggestion = _l === void 0 ? defaultMatchSuggestion(getChoiceText) : _l, selectedItem = _a.selectedItem, _m = _a.suggestionLimit, suggestionLimit = _m === void 0 ? 0 : _m;
    return function (filter) {
        var suggestions = [];
        // if an item is selected and matches the filter
        if (selectedItem &&
            !Array.isArray(selectedItem) &&
            matchSuggestion(filter, selectedItem)) {
            if (limitChoicesToValue) {
                // display only the selected item
                suggestions = choices.filter(function (choice) {
                    return getChoiceValue(choice) === getChoiceValue(selectedItem);
                });
            }
            else {
                suggestions = __spreadArray([], choices, true);
            }
        }
        else {
            suggestions = choices.filter(function (choice) {
                return matchSuggestion(filter, choice) ||
                    (selectedItem != null &&
                        (!Array.isArray(selectedItem)
                            ? getChoiceValue(choice) ===
                                getChoiceValue(selectedItem)
                            : selectedItem.some(function (selected) {
                                return getChoiceValue(choice) ===
                                    getChoiceValue(selected);
                            })));
            });
        }
        suggestions = limitSuggestions(suggestions, suggestionLimit);
        var hasExactMatch = suggestions.some(function (suggestion) {
            return matchSuggestion(filter, suggestion, true);
        });
        if (allowCreate) {
            var filterIsSelectedItem = 
            // If the selectedItem is an array (for example AutocompleteArrayInput)
            // we shouldn't try to match
            !!selectedItem && !Array.isArray(selectedItem)
                ? matchSuggestion(filter, selectedItem, true)
                : false;
            if (!hasExactMatch && !filterIsSelectedItem) {
                suggestions.push(getSuggestion({
                    optionText: optionText,
                    optionValue: optionValue,
                    text: createText,
                    value: createValue,
                }));
            }
        }
        // Only keep unique items. Necessary because we might have fetched
        // the currently selected choice in addition of the possible choices
        // that may also contain it
        var result = suggestions.filter(function (suggestion, index) { return suggestions.indexOf(suggestion) === index; });
        return result;
    };
};
/**
 * @example
 *
 * limitSuggestions(
 *  [{ id: 1, name: 'foo'}, { id: 2, name: 'bar' }],
 *  1
 * );
 *
 * // Will return [{ id: 1, name: 'foo' }]
 *
 * @param suggestions List of suggestions
 * @param limit
 */
var limitSuggestions = function (suggestions, limit) {
    if (limit === void 0) { limit = 0; }
    return Number.isInteger(limit) && limit > 0
        ? suggestions.slice(0, limit)
        : suggestions;
};
/**
 * addSuggestion(
 *  [{ id: 1, name: 'foo'}, { id: 2, name: 'bar' }],
 * );
 *
 * // Will return [{ id: null, name: '' }, { id: 1, name: 'foo' }, , { id: 2, name: 'bar' }]
 *
 * @param suggestions List of suggestions
 * @param options
 * @param options.optionText
 */
var getSuggestion = function (_a) {
    var _b = _a.optionText, optionText = _b === void 0 ? 'name' : _b, _c = _a.optionValue, optionValue = _c === void 0 ? 'id' : _c, _d = _a.text, text = _d === void 0 ? '' : _d, _e = _a.value, value = _e === void 0 ? null : _e;
    var suggestion = {};
    set(suggestion, optionValue, value);
    if (typeof optionText === 'string') {
        set(suggestion, optionText, text);
    }
    return suggestion;
};
//# sourceMappingURL=useSuggestions.js.map